import React, { useCallback } from 'react';
import { observer } from 'mobx-react';

import { RequestQuoteForm } from '@canvas-logic/clp-shared-ui';

import { ContactUsService } from '@/ui-kit/services/ContactUsService';
import { RequestQuoteFormStatus } from '@/ui-kit/components/RequestQuote/RequestQuoteFormStatus';
import { useLanguageSwitcher } from '@/hooks/useLanguageSwitcher';
import { useContactFormFieldLabels } from '@/localization/useContactFormFieldLabels';
import { useContactFormOverrides } from '@/localization/useContactFormOverrides';
import { embeddingService } from '@/ui-kit/services/embedding/EmbeddingService';
import { getUrlRoutingKey } from '@/ui-kit/services/getUrlRoutingKey';

import styles from './RequestQuote.module.css';

interface RequestQuoteProps {
  contactUsService: ContactUsService;
  createLink?: () => Promise<string>;
}

export const RequestQuote = observer(function RequestQuote({ contactUsService, createLink }: RequestQuoteProps) {
  const { requestStatus } = contactUsService;
  const { currentLanguage } = useLanguageSwitcher();
  const fieldLabels = useContactFormFieldLabels(contactUsService.schema);
  const overrides = useContactFormOverrides();

  const onSubmit = useCallback(
    async (formData: Record<string, string>) => {
      let data = formData;

      if (createLink) {
        const link = await createLink();
        data = { ...formData, configurationLink: link };
      }

      // `routing_key` from the direct link or the iframe host page drives Advanced Routing.
      // Resolved at submit time so the iframe host handshake is complete.
      const routingKey = getUrlRoutingKey(window.location, embeddingService.initialLocation);
      await contactUsService.requestQuote(data, routingKey);
    },
    [contactUsService, createLink],
  );

  return (
    <div className={styles.container}>
      {requestStatus !== 'idle' ? (
        <RequestQuoteFormStatus status={requestStatus} />
      ) : (
        <RequestQuoteForm
          schema={contactUsService.schema}
          onSubmit={onSubmit}
          lang={currentLanguage}
          fieldLabels={fieldLabels}
          overrides={overrides}
        />
      )}
    </div>
  );
});
